import { useEffect, useRef, useState } from 'react';
import Pagination from './Pagination';
import CallHistoryModal from './CallHistoryModal';
import RecordingPlayer from './RecordingPlayer';
import DateRangePicker from './DateRangePicker';
import SelectMenu from './SelectMenu';
import {
  getCallStatus,
  formatDuration,
  formatTimestamp,
  groupByCustomer,
  getUnansweredStreak,
  getKnownAgents,
  isCritical,
  filterCallsByDate,
  localDateStr,
  QUERY_TYPE_OPTIONS,
  isInternationalNumber,
} from './callUtils';
import { useAuth, isRefreshFor } from './AuthContext';
import LiveCallBadge, { indexLiveCalls, clockOffsetFrom } from './LiveCallBadge';
import { PhoneIcon, ChevronRightIcon, DownloadIcon, SearchIcon } from './Icons';
import { downloadCsv } from './csvExport';

const DEFAULT_PAGE_SIZE = 50;
const ALERTS_ENABLED_KEY = 'incomingCalls.alertsEnabled';
const HISTORY_NUMBER_KEY = 'incomingCalls.historyNumber';
const FORCE_QUERY_ON_OPEN_KEY = 'incomingCalls.forceQueryOnOpen';

// Short beep via Web Audio so a missed-call alert doesn't depend on an audio
// asset file — just an oscillator blip, silently skipped if unsupported.
const playAlertTone = () => {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.value = 880;
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.4);
    oscillator.onended = () => ctx.close();
  } catch (error) {
    console.error('Error playing missed call alert tone:', error);
  }
};

function IncomingCalls({ calls }) {
  const { user, authFetch, subscribeRefresh } = useAuth();
  const [filter, setFilter] = useState('Missed');
  const [agentFilter, setAgentFilter] = useState('All');
  const [queryTypeFilter, setQueryTypeFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // Restored from sessionStorage on mount so a browser refresh keeps the
  // agent on the call-history modal they had open instead of dumping them
  // back to the plain list — important since a compulsory, unsaved query
  // type/query entry (see forceQueryOnOpen) would otherwise be lost.
  const [historyNumber, setHistoryNumber] = useState(() => {
    try {
      return sessionStorage.getItem(HISTORY_NUMBER_KEY) || null;
    } catch {
      return null;
    }
  });
  const [forceQueryOnOpen, setForceQueryOnOpen] = useState(() => {
    try {
      return sessionStorage.getItem(FORCE_QUERY_ON_OPEN_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [ringingNumbers, setRingingNumbers] = useState(new Set());
  const [ringingAgents, setRingingAgents] = useState(new Map());
  // Calls in progress per Tata (ringing, who's talking to whom), by
  // customer number. Same request and polling as the ringing badge.
  const [liveCalls, setLiveCalls] = useState(new Map());
  const [clockOffsetMs, setClockOffsetMs] = useState(0);
  const [viewingAgents, setViewingAgents] = useState(new Map());
  const [customerNames, setCustomerNames] = useState(new Map());
  const [alertsEnabled, setAlertsEnabled] = useState(() => {
    try {
      return localStorage.getItem(ALERTS_ENABLED_KEY) !== 'false';
    } catch {
      return true;
    }
  });
  const knownAgents = getKnownAgents(calls);
  const agentNameByNumber = new Map(knownAgents.map((agent) => [agent.number, agent.name]));
  const seenMissedUuidsRef = useRef(null);

  const fetchRinging = async () => {
    try {
      const response = await authFetch('/api/ringing-calls');
      const data = await response.json();
      setRingingNumbers(new Set(data.numbers || []));
      setRingingAgents(new Map(Object.entries(data.agentByNumber || {})));
      setLiveCalls(indexLiveCalls(data.live));
      setClockOffsetMs(clockOffsetFrom(data.serverTime));
    } catch (error) {
      console.error('Error fetching ringing calls:', error);
    }
  };

  // Who else currently has this number's call-history modal open — shown as
  // a "Viewing — agentName" badge so a second agent can see someone's
  // already on it before opening the same call blind.
  const fetchViewing = async () => {
    try {
      const response = await authFetch('/api/viewing-calls');
      const data = await response.json();
      setViewingAgents(new Map(Object.entries(data.agentsByNumber || {})));
    } catch (error) {
      console.error('Error fetching viewing calls:', error);
    }
  };

  // Names saved against a number from the call-history modal (see
  // CallHistoryModal) — fetched once and refreshed on the same SSE signal as
  // ringing calls so a name an agent just saved shows up here without a
  // manual reload.
  const fetchCustomerNames = async () => {
    try {
      const response = await authFetch('/api/customers');
      const data = await response.json();
      setCustomerNames(
        new Map((Array.isArray(data) ? data : []).map((customer) => [customer.number, customer.name]))
      );
    } catch (error) {
      console.error('Error fetching customer names:', error);
    }
  };

  // Live-updated via the shared SSE connection so a ring shows up on every
  // agent's screen within a beat, not just the one who placed the call. The
  // interval below is only a safety net in case that connection is blocked.
  useEffect(() => {
    fetchRinging();
    fetchViewing();
    fetchCustomerNames();
    // WhatsApp, email and cart events can't change ringing, viewing or
    // customer names (customer profiles are only saved by PUT
    // /api/customers/:number, which sends no event), so they're skipped.
    const unsubscribe = subscribeRefresh((event) => {
      if (!isRefreshFor(event, 'calls', 'ringing', 'viewing')) return;
      fetchRinging();
      fetchViewing();
      fetchCustomerNames();
    });
    // Short fallback poll — SSE delivers the update within a beat when it
    // gets through, but some networks (corporate proxies/AV doing TLS
    // inspection) silently buffer or drop long-lived SSE connections without
    // erroring, so this keeps the worst-case lag low instead of relying on
    // SSE alone.
    const interval = setInterval(() => {
      fetchRinging();
      fetchViewing();
    }, 3000);
    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [subscribeRefresh]);

  useEffect(() => {
    try {
      localStorage.setItem(ALERTS_ENABLED_KEY, String(alertsEnabled));
    } catch {
      // ignore storage errors (e.g. private browsing)
    }
    if (alertsEnabled && typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [alertsEnabled]);

  // Only persist the open modal across a page switch when a call was
  // actually placed from it (forceQueryOnOpen) — just viewing history
  // without calling should never reopen it after navigating away and back.
  useEffect(() => {
    try {
      if (historyNumber && forceQueryOnOpen) {
        sessionStorage.setItem(HISTORY_NUMBER_KEY, historyNumber);
      } else {
        sessionStorage.removeItem(HISTORY_NUMBER_KEY);
      }
      sessionStorage.setItem(FORCE_QUERY_ON_OPEN_KEY, String(forceQueryOnOpen));
    } catch {
      // ignore storage errors (e.g. private browsing)
    }
  }, [historyNumber, forceQueryOnOpen]);

  // Alerts on the live, unfiltered call list — a missed call should be heard
  // regardless of which status/date/agent filter is currently on screen. The
  // baseline is captured on the first run so existing history never alerts.
  useEffect(() => {
    const missedUuids = new Set(
      calls.filter((call) => call.uuid && getCallStatus(call) === 'Missed').map((call) => call.uuid)
    );

    if (seenMissedUuidsRef.current === null) {
      seenMissedUuidsRef.current = missedUuids;
      return;
    }

    const hasNewMiss = [...missedUuids].some((uuid) => !seenMissedUuidsRef.current.has(uuid));
    seenMissedUuidsRef.current = missedUuids;

    if (hasNewMiss && alertsEnabled) {
      playAlertTone();
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        new Notification('Missed call', { body: 'A customer call was just missed.' });
      }
    }
  }, [calls, alertsEnabled]);

  const today = localDateStr(new Date());
  const weekAgo = localDateStr(new Date(Date.now() - 6 * 24 * 60 * 60 * 1000));
  const datePreset =
    !fromDate && !toDate
      ? 'all'
      : fromDate === today && toDate === today
      ? 'today'
      : fromDate === weekAgo && toDate === today
      ? 'week'
      : 'custom';
  const hasDateFilter = Boolean(fromDate || toDate);

  const applyDatePreset = (preset) => {
    if (preset === 'today') {
      setFromDate(today);
      setToDate(today);
    } else if (preset === 'week') {
      setFromDate(weekAgo);
      setToDate(today);
    } else {
      setFromDate('');
      setToDate('');
    }
  };

  const dateFilteredCalls = filterCallsByDate(calls, fromDate, toDate);
  const groupedCalls = groupByCustomer(dateFilteredCalls);
  
  // Separate international calls from regular calls
  const internationalCalls = groupedCalls.filter((group) => isInternationalNumber(group.customerNumber));
  const regularCalls = groupedCalls.filter((group) => !isInternationalNumber(group.customerNumber));
  
  // Combined calls for history number check
  const allCalls = [...internationalCalls, ...regularCalls];
  
  const statusFilteredCalls =
    filter === 'International'
      ? internationalCalls
      : filter === 'All'
      ? regularCalls
      : filter === 'Critical'
      ? regularCalls.filter((group) => isCritical(group))
      : regularCalls.filter((group) => getCallStatus(group.latestCall) === filter);
  const agentFilteredCalls =
    agentFilter === 'All'
      ? statusFilteredCalls
      : statusFilteredCalls.filter((group) => group.latestCall.answered_agent_number === agentFilter);

  // query_type lives on whichever call it was saved against, not necessarily
  // the customer's latest one (see QueryPanel's queryCallByNumber) — a later
  // contentless missed call shouldn't blank out an earlier saved query type.
  const queryTypeByNumber = new Map(
    groupedCalls.map((group) => [
      group.customerNumber,
      (group.callsNewestFirst.find((call) => call.query_type) || {}).query_type || null,
    ])
  );
  const queryTypeFilteredCalls =
    queryTypeFilter === 'All'
      ? agentFilteredCalls
      : agentFilteredCalls.filter((group) => queryTypeByNumber.get(group.customerNumber) === queryTypeFilter);

  const searchTerm = search.trim().toLowerCase();
  const searchedCalls = !searchTerm
    ? queryTypeFilteredCalls
    : queryTypeFilteredCalls.filter((group) => {
        const call = group.latestCall;
        return (
          group.customerNumber.toLowerCase().includes(searchTerm) ||
          (call.answered_agent_name || '').toLowerCase().includes(searchTerm) ||
          (customerNames.get(group.customerNumber) || '').toLowerCase().includes(searchTerm)
        );
      });

  // The missed/critical card list is a callback queue, not a feed — the
  // customer waiting longest should be handled first, so it sorts oldest to
  // newest here instead of the newest-first order everything else keeps.
  const isCallbackQueue = filter === 'Missed' || filter === 'Critical' || filter === 'International';
  const filteredCalls = isCallbackQueue
    ? [...searchedCalls].sort((a, b) =>
        (a.latestCall.start_stamp || '').localeCompare(b.latestCall.start_stamp || '')
      )
    : searchedCalls;

  const totalPages = Math.max(1, Math.ceil(filteredCalls.length / pageSize));

  // Exports exactly what's currently filtered/searched — same rows the
  // table/card list is showing, not the full unfiltered call history.
  const handleExportCsv = () => {
    const headers = ['Customer Number', 'Status', 'Agent Name', 'Duration', 'Digit Pressed', 'Recording URL', 'Last Call Time', 'Attempts'];
    const rows = filteredCalls.map((group) => {
      const call = group.latestCall;
      const status = getCallStatus(call);
      return [
        group.customerNumber,
        status,
        call.answered_agent_name || '',
        formatDuration(call),
        call.digit_pressed || '',
        call.recording_url || '',
        formatTimestamp(call.start_stamp),
        status === 'Answered' ? 1 : getUnansweredStreak(group),
      ];
    });
    downloadCsv(`incoming-calls-${localDateStr(new Date())}.csv`, headers, rows);
  };

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    setPage(1);
  }, [filter, agentFilter, queryTypeFilter, search, fromDate, toDate]);

  // A historyNumber restored from sessionStorage (see above) can point at a
  // customer that no longer exists in the loaded calls — e.g. after a data
  // reset. Once real data is in, drop it rather than rendering a modal for a
  // group that will never be found.
  useEffect(() => {
    if (!historyNumber) return;
    const exists = allCalls.some((group) => group.customerNumber === historyNumber);
    if (!exists) {
      setHistoryNumber(null);
      setForceQueryOnOpen(false);
    }
  }, [historyNumber, calls, allCalls]);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  const pageCalls = filteredCalls.slice((page - 1) * pageSize, page * pageSize);

  // Excludes the current agent's own name — the point is warning about
  // someone *else* already on this call, not flagging your own open modal.
  const getViewerNames = (customerNumber) =>
    (viewingAgents.get(customerNumber) || []).filter((name) => name !== user?.username);

  const totalCalls = regularCalls.length;
  const totalAnswered = regularCalls.filter(
    (group) => getCallStatus(group.latestCall) === 'Answered'
  ).length;
  const totalMissed = regularCalls.filter(
    (group) => getCallStatus(group.latestCall) === 'Missed'
  ).length;
  const totalAttempted = regularCalls.filter(
    (group) => getCallStatus(group.latestCall) === 'Attempted'
  ).length;
  const totalCritical = regularCalls.filter((group) => isCritical(group)).length;
  const totalInternational = internationalCalls.length;

  return (
    <>
      <div className="incoming-filters-bar">
        <div className="customer-toggle" role="group" aria-label="Date range">
          <button type="button" className={datePreset === 'all' ? 'active' : ''} onClick={() => applyDatePreset('all')}>
            All
          </button>
          <button type="button" className={datePreset === 'today' ? 'active' : ''} onClick={() => applyDatePreset('today')}>
            Today
          </button>
          <button type="button" className={datePreset === 'week' ? 'active' : ''} onClick={() => applyDatePreset('week')}>
            This Week
          </button>
        </div>
        <div className="agent-date-filter">
          <DateRangePicker
            fromDate={fromDate}
            toDate={toDate}
            onApply={(from, to) => {
              setFromDate(from);
              setToDate(to);
            }}
          />
          {hasDateFilter && (
            <button type="button" className="agent-date-filter-clear" onClick={() => applyDatePreset('all')}>
              Clear
            </button>
          )}
        </div>
        <div className="search-input-wrap">
          <SearchIcon className="search-input-icon" width={15} height={15} />
          <input
            type="text"
            className="search-input"
            placeholder="Search by customer number, name, or agent"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Search by customer number, customer name, or agent name"
          />
        </div>
        <SelectMenu
          className="incoming-agent-filter"
          value={agentFilter}
          onChange={setAgentFilter}
          ariaLabel="Filter by agent"
          options={[
            { value: 'All', label: 'All Agents' },
            ...knownAgents.map((agent) => ({ value: agent.number, label: agent.name })),
          ]}
        />
        <SelectMenu
          className="incoming-query-type-filter"
          value={queryTypeFilter}
          onChange={setQueryTypeFilter}
          ariaLabel="Filter by query type"
          options={[{ value: 'All', label: 'All Query Types' }, ...QUERY_TYPE_OPTIONS]}
        />
        <label className="alerts-toggle">
          <input
            type="checkbox"
            checked={alertsEnabled}
            onChange={(event) => setAlertsEnabled(event.target.checked)}
          />
          Sound alert on missed calls
        </label>
        <button type="button" className="btn btn-secondary export-csv-btn" onClick={handleExportCsv}>
          <DownloadIcon width={14} height={14} />
          Export CSV
        </button>
      </div>
      <div className="stat-cards">
        <button
          className={`stat-card ${filter === 'All' ? 'active' : ''}`}
          onClick={() => setFilter('All')}
        >
          <div className="stat-value">{totalCalls}</div>
          <div className="stat-label">Total Calls</div>
        </button>
        <button
          className={`stat-card answered ${filter === 'Answered' ? 'active' : ''}`}
          onClick={() => setFilter('Answered')}
        >
          <div className="stat-value">{totalAnswered}</div>
          <div className="stat-label">Answered</div>
        </button>
        <button
          className={`stat-card missed ${filter === 'Missed' ? 'active' : ''}`}
          onClick={() => setFilter('Missed')}
        >
          <div className="stat-value">{totalMissed}</div>
          <div className="stat-label">Missed</div>
        </button>
        <button
          className={`stat-card attempted ${filter === 'Attempted' ? 'active' : ''}`}
          onClick={() => setFilter('Attempted')}
        >
          <div className="stat-value">{totalAttempted}</div>
          <div className="stat-label">Called Back, No Answer</div>
        </button>
        <button
          className={`stat-card critical ${filter === 'Critical' ? 'active' : ''}`}
          onClick={() => setFilter('Critical')}
        >
          <div className="stat-value">{totalCritical}</div>
          <div className="stat-label">Critical</div>
        </button>
        <button
          className={`stat-card international ${filter === 'International' ? 'active' : ''}`}
          onClick={() => setFilter('International')}
        >
          <div className="stat-value">{totalInternational}</div>
          <div className="stat-label">International</div>
        </button>
      </div>
      {filter === 'Missed' || filter === 'Critical' || filter === 'International' ? (
        <div className="missed-card-list">
          {pageCalls.length === 0 ? (
            <p className="agent-page-empty">No calls to show.</p>
          ) : (
            pageCalls.map((group) => {
              const call = group.latestCall;
              const unansweredStreak = getUnansweredStreak(group);
              const liveCall = liveCalls.get(group.customerNumber);
              const isRinging = !liveCall && ringingNumbers.has(group.customerNumber);
              const ringingAgentName = agentNameByNumber.get(ringingAgents.get(group.customerNumber));
              const viewerNames = getViewerNames(group.customerNumber);
              const critical = isCritical(group);

              return (
                <button
                  key={group.customerNumber}
                  type="button"
                  className={`missed-card ${isRinging || liveCall ? 'row-ringing' : ''} ${critical ? 'missed-card-critical' : ''}`}
                  onClick={() => setHistoryNumber(group.customerNumber)}
                  title="Open chat"
                >
                  <span className="missed-card-avatar">
                    <PhoneIcon width={16} height={16} />
                  </span>
                  <span className="missed-card-body">
                    <span className="missed-card-top">
                      <span className="missed-card-number">
                        {group.customerNumber}
                        {customerNames.get(group.customerNumber) && (
                          <span className="missed-card-customer-name"> ({customerNames.get(group.customerNumber)})</span>
                        )}
                      </span>
                      <span className="missed-card-time">{formatTimestamp(call.start_stamp)}</span>
                    </span>
                    <span className="missed-card-bottom">
                      <span className="status missed">Missed</span>
                      {critical && <span className="status critical">Critical</span>}
                      {unansweredStreak > 1 && (
                        <span className="call-count">{unansweredStreak} attempts</span>
                      )}
                      {call.digit_pressed && (
                        <span className="call-count">Pressed {call.digit_pressed}</span>
                      )}
                      {liveCall && <LiveCallBadge call={liveCall} clockOffsetMs={clockOffsetMs} />}
                      {isRinging && (
                        <span className="status ringing">
                          Calling{ringingAgentName ? ` — ${ringingAgentName}` : ''}
                        </span>
                      )}
                      {viewerNames.length > 0 && (
                        <span className="status viewing">Viewing — {viewerNames.join(', ')}</span>
                      )}
                    </span>
                  </span>
                  <ChevronRightIcon className="missed-card-chevron" width={18} height={18} />
                </button>
              );
            })
          )}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Customer Number</th>
                <th>Call Status</th>
                <th>Agent Name</th>
                <th>Duration</th>
                <th>Digit Pressed</th>
                <th>Recording</th>
              </tr>
            </thead>
            <tbody>
              {filteredCalls.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center' }}>
                    No calls to show.
                  </td>
                </tr>
              ) : (
                pageCalls.map((group) => {
                  const status = getCallStatus(group.latestCall);
                  const unansweredStreak =
                    status !== 'Answered' ? getUnansweredStreak(group) : 0;
                  const call = group.latestCall;

                  const liveCall = liveCalls.get(group.customerNumber);
                  const isRinging = !liveCall && ringingNumbers.has(group.customerNumber);
                  const ringingAgentName = agentNameByNumber.get(ringingAgents.get(group.customerNumber));
                  const viewerNames = getViewerNames(group.customerNumber);

                  return (
                    <tr key={group.customerNumber} className={isRinging || liveCall ? 'row-ringing' : ''}>
                      <td>
                        <button
                          className="customer-number-link"
                          onClick={() => setHistoryNumber(group.customerNumber)}
                          title="View call history for this number"
                        >
                          {group.customerNumber}
                          {customerNames.get(group.customerNumber) && (
                            <span className="customer-number-name"> ({customerNames.get(group.customerNumber)})</span>
                          )}
                        </button>
                        {unansweredStreak > 1 && (
                          <span className="call-count"> ({unansweredStreak})</span>
                        )}
                      </td>
                      <td>
                        <span className={`status ${status.toLowerCase()}`}>{status}</span>
                        {liveCall && <LiveCallBadge call={liveCall} clockOffsetMs={clockOffsetMs} />}
                        {isRinging && (
                          <span className="status ringing">
                            Calling{ringingAgentName ? ` — ${ringingAgentName}` : ''}
                          </span>
                        )}
                        {viewerNames.length > 0 && (
                          <span className="status viewing">Viewing — {viewerNames.join(', ')}</span>
                        )}
                      </td>
                      <td>
                        {liveCall?.phase === 'connected' && liveCall.agentName
                          ? liveCall.agentName
                          : call.answered_agent_name || '-'}
                      </td>
                      <td>{formatDuration(call)}</td>
                      <td>{call.digit_pressed || '-'}</td>
                      <td>
                        <RecordingPlayer url={call.recording_url} />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        totalItems={filteredCalls.length}
      />
      {historyNumber && (
        <CallHistoryModal
          group={groupedCalls.find((group) => group.customerNumber === historyNumber)}
          onClose={() => {
            setHistoryNumber(null);
            setForceQueryOnOpen(false);
            fetchCustomerNames();
          }}
          agents={knownAgents}
          onCallTriggered={() => {
            fetchRinging();
            // A call placed from the modal's own Call button must survive a
            // refresh too — otherwise reloading right after calling would
            // reopen the modal without the compulsory query requirement.
            setForceQueryOnOpen(true);
          }}
          initialCallPlaced={forceQueryOnOpen}
          isRinging={!liveCalls.has(historyNumber) && ringingNumbers.has(historyNumber)}
          liveCall={liveCalls.get(historyNumber)}
          clockOffsetMs={clockOffsetMs}
          ringingAgentName={agentNameByNumber.get(ringingAgents.get(historyNumber))}
          viewerNames={getViewerNames(historyNumber)}
        />
      )}
    </>
  );
}

export default IncomingCalls;
